import { CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';
import { isScrolledIntoView } from '@bs/utils';

/**
 * In Viewport
 */
export default class InViewport {
  /**
   * Constructor for inviewport which simply assigns the ScrollService
   * to a property on the contructor for reference.
   * @param {object} Services various services, passed in as param
   */
  constructor(Services) {
    /**
     * Reference to the ScrollService singleton
     * @property {object} ScrollService - Instance of the ScrollService
     */
    this.ScrollService = Services.ScrollService;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  cacheDomReferences() {
    /**
     * All DOM elements with the `data-visible` attribute
     * @property {NodeList} modules - List of DOM elements with the `data-visible` attribute
     */
    this.modules = document.querySelectorAll(SELECTORS.DATA_VISIBLE);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  setupHandlers() {
    /**
     * A reference to the `onScroll` function with the proper context bound to the InViewport class.
     * @property {Function} onScrollHandler - Reference to the `onScroll` function with bound context
     */
    this.onScrollHandler = this.onScroll.bind(this);

    /**
     * A reference to the `updateModules` function with the proper
     * context bound to the InViewport class.
     * @property {Function} onModuleUpdateHandler - Reference to the `updateModules` function with bound context
     */
    this.onModuleUpdateHandler = this.updateModules.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  enable() {
    // Call scroll handler on load to get initial viewable elements
    window.setTimeout(this.onScrollHandler, 500);

    // Add to ScrollSerive callbacks
    this.ScrollService.addCallback(this.onScrollHandler);

    document.body.addEventListener(
      EVENTS.UPDATE_IN_VIEWPORT_MODULES,
      this.onModuleUpdateHandler
    );

    return this;
  }

  /**
   * A function which loops over the current modules and determines
   * which are currently in the viewport. Depending on whether or
   * not they are visible a data attribute boolean is toggled
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  onScroll() {
    Array.prototype.forEach.call(this.modules, (module) => {
      if (isScrolledIntoView(module)) {
        if (module.getAttribute(MISC.DATA_VISIBLE) === 'false') {
          module.setAttribute(MISC.DATA_VISIBLE, true);
        }
        if (!module.hasAttribute(SELECTORS.DATA_HAS_ANIMATED)) {
          module.setAttribute(SELECTORS.DATA_HAS_ANIMATED, true);
        }
      } else if (module.getAttribute(MISC.DATA_VISIBLE) === 'true') {
        module.setAttribute(MISC.DATA_VISIBLE, false);
      }
      const rect = module.getBoundingClientRect();
      const currentDataPosition = module.getAttribute(SELECTORS.DATA_POSITION);
      const belowDataClass =
        rect.top >= window.innerHeight
          ? CLASS_NAMES.BELOW_VIEWPORT
          : CLASS_NAMES.IN_VIEWPORT;
      const calculatedDataPosition =
        rect.bottom < 0 ? CLASS_NAMES.ABOVE_VIEWPORT : belowDataClass;
      const calculatedBottomPosition =
        rect.bottom > window.innerHeight
          ? CLASS_NAMES.BELOW_BOTTOM
          : CLASS_NAMES.ABOVE_BOTTOM;
      const halfwayPosition =
        rect.bottom <= window.innerHeight / 1.25
          ? CLASS_NAMES.ABOVE_HALFWAY
          : CLASS_NAMES.BELOW_HALFWAY;
      if (currentDataPosition !== calculatedDataPosition) {
        module.setAttribute(SELECTORS.DATA_POSITION, calculatedDataPosition);
      }
      module.setAttribute(SELECTORS.DATA_BOTTOM, calculatedBottomPosition);
      module.setAttribute(SELECTORS.DATA_HALFWAY, halfwayPosition);
    });

    return this;
  }

  /**
   * A function which updates the list of data-visible modules by calling `cacheDomReferences` and calls `onScroll`
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  updateModules() {
    this.cacheDomReferences().onScroll();

    return this;
  }
}
